import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Card, Button } from 'react-bootstrap';
import { useHistory, useLocation } from 'react-router-dom';
import Product from '../components/Product';
import Loader from '../components/Loader';
import Message from '../components/Message';
import Paginate from '../components/Paginate';
import ProductCarousel from '../components/ProductCarousel';
import { listProducts } from '../actions/productActions';

// TractorTypeCard Component
const TractorTypeCard = ({ type, description, imageUrl, onSelectType }) => {
    return (
        <Card className="my-3 p-3 rounded" onClick={() => onSelectType(type)}>
            <Card.Img variant="top" src={imageUrl} />
            <Card.Body>
                <Card.Text>
                    {description}
                </Card.Text>
            </Card.Body>
        </Card>
    );
};

// HomeScreen Component
function HomeScreen() {
    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();

    const productList = useSelector((state) => state.productList);
    const { error, loading, products, page, pages } = productList;

    const userLogin = useSelector((state) => state.userLogin);
    const { userInfo } = userLogin;

    const [selectedType, setSelectedType] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    let keyword = new URLSearchParams(location.search).get('keyword') || '';
    let pageParam = new URLSearchParams(location.search).get('page') || 1;

    useEffect(() => {
        dispatch(listProducts(keyword, selectedType, currentPage));
    }, [dispatch, keyword, selectedType, currentPage]);

    // Function to handle tractor type selection
    const handleSelectType = (type) => {
        setSelectedType(type);
        setCurrentPage(1); // Reset pagination to the first page on filter change
    };

    // Function to clear all filters
    const clearFilters = () => {
        setSelectedType('');
        setCurrentPage(1); // Reset pagination to the first page on filter clear
    };

    // Update URL when currentPage changes
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        queryParams.set('page', currentPage);
        if (!keyword) {
            queryParams.delete('keyword'); // Remove keyword if it's empty
        }
        history.push({ search: queryParams.toString() });
    }, [currentPage, history, location.search]);

    // Reset filters on search
    useEffect(() => {
        if (keyword) {
            setSelectedType(''); // Clear the selected type when performing a search
            setCurrentPage(1); // Reset pagination
        }
    }, [keyword]);

    // Define the tractor types with their descriptions and images
    const tractorTypes = [
        {
            type: 'subcompact',
            description: 'Ideal for small farming and large-scale gardening.',
            imageUrl: `https://${process.env.REACT_APP_LINODE_BUCKET}.${process.env.REACT_APP_LINODE_BUCKET_REGION}.linodeobjects.com/images/subcompact.png`
        },
        {
            type: 'compact',
            description: 'Versatile and powerful, perfect for a variety of tasks.',
            imageUrl: `https://${process.env.REACT_APP_LINODE_BUCKET}.${process.env.REACT_APP_LINODE_BUCKET_REGION}.linodeobjects.com/images/compact.png`
        },
        {
            type: 'utility',
            description: 'Robust and efficient for heavy-duty farming tasks.',
            imageUrl: `https://${process.env.REACT_APP_LINODE_BUCKET}.${process.env.REACT_APP_LINODE_BUCKET_REGION}.linodeobjects.com/images/utility.png`
        }
    ];

    return (
        <div>
            {!keyword && <ProductCarousel />}
            {!keyword && (
                <h4 style={{ fontWeight: "normal", fontSize: "1.2rem", color: "#5c5c5c" }}>
                    Here you can build a quote for a tractor and send it to your local dealer!<br />
                    Get ready to be inspired, motivated, and connected with our amazing community. <br />
                    Thank you for choosing <span style={{ fontWeight: "bold", color: "#2476dc" }}>LS Tractor USA.</span>
                </h4>
            )}
            {!keyword && <hr />}

            <h3>Our Products</h3>

            {/* Tractor Type Filter Section */}
            {!keyword && (
                <Row>
                    {tractorTypes.map(({ type, description, imageUrl }) => (
                        <Col key={type} sm={12} md={4}>
                            <TractorTypeCard
                                type={type}
                                description={description}
                                imageUrl={imageUrl}
                                onSelectType={handleSelectType}
                            />
                        </Col>
                    ))}
                </Row>
            )}

            <hr /><br />

            {loading ? (
                <Loader />
            ) : error ? (
                <Message variant="danger">{error}</Message>
            ) : (
                <div>
                    <Row>
                        {products.map((product) => (
                            <Col key={product._id} sm={12} md={6} lg={4} xl={3}>
                                <Product product={product} />
                            </Col>
                        ))}
                    </Row>
                    <Paginate 
                        page={page} 
                        pages={pages} 
                        keyword={keyword} 
                        setPageNumber={setCurrentPage} // Pass setPageNumber to handle pagination changes
                    />
                </div>
            )}
            {/* Clear Filters Button */}
            {!keyword && (
                <div className="text-center my-3">
                    <Button variant="primary rounded" onClick={clearFilters}>Reset View</Button>
                </div>
            )}
        </div>
    );
}

export default HomeScreen;
